"use client";

import { useState } from "react";
import { useToolLocale } from "@/components/tools/tool-ui";

/**
 * The one control the seven exact-transformation tools share.
 *
 * A tool whose entire job is casing text cannot guess which language the text is in.
 * The page's locale is the *interface* language, not the content's: a German reader may
 * paste Turkish, and a Turkish reader on `/tr` may paste English — inheriting the page
 * locale silently breaks the second case exactly as ignoring it breaks the first. So the
 * tool asks, and starts from the likelier answer.
 *
 * Auto-detection was considered and rejected: sniffing the input for `ı İ ş ğ` is right
 * for "İstanbul" and wrong for "ILGI", and a transformation tool that is silently right
 * most of the time is worse than one that asks once.
 *
 * Turkish-specific rather than a language picker because the data says so — sixteen of
 * the site's seventeen locales fold identically, so a select would offer sixteen
 * identical options. The label names the distinction the reader can actually see rather
 * than a language, which is also what makes it useful to a Turkish speaker reading the
 * English page — the reported bug.
 */
export interface TurkishCaseStrings {
  turkishLabel: string;
  turkishHint: string;
}

/** English defaults, so a widget can spread them into its own `*_STRINGS`. */
export const TURKISH_CASE_STRINGS: TurkishCaseStrings = {
  turkishLabel: "Turkish i rules (İ ı)",
  turkishHint:
    "Turkish is the only language that cases the letter i differently: İ lowers to i, and i raises to İ.",
};

/** `true` when the reader is on the Turkish tree — a default, never a lock. */
export function useTurkishCase(): [boolean, (next: boolean) => void] {
  const locale = useToolLocale();
  const [turkish, setTurkish] = useState(locale === "tr");
  return [turkish, setTurkish];
}

export function TurkishCaseToggle({
  checked,
  onChange,
  strings,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  strings: TurkishCaseStrings;
}) {
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 text-foreground text-sm">
        <input
          checked={checked}
          className="size-4 rounded border-border"
          onChange={(event) => onChange(event.target.checked)}
          type="checkbox"
        />
        {strings.turkishLabel}
      </label>
      <p className="text-muted-foreground text-xs leading-relaxed">
        {strings.turkishHint}
      </p>
    </div>
  );
}
